import { DistributionChannel } from '@ail/notifications-api'
import { NotificationsDataRow } from './NotificationsAdminPanel'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import TemplateEditor from './TemplateEditor'

const CHANNEL_CONFIG: Record<DistributionChannel, { icon: string; empty: string; className: string }> = {
  [DistributionChannel.Native]: { icon: 'icon-alert', empty: 'No native templates', className: 'nativeTmp' },
  [DistributionChannel.Email]: { icon: 'icon-mail', empty: 'No email templates', className: 'emailTmp' },
}
/**
 * ' * ' stands for default language (EN on display)
 * @example
 * ['*', 'de', 'fr', 'it']
 */
const TEMPLATE_LANGS = ['*', 'de', 'fr', 'it']

interface Props {
  row: NotificationsDataRow
  onEditorToggle: (toggledEditorsCount: number) => void
}

export default function NotificationGroupDetails({ row, onEditorToggle }: Props) {
  const [selectedLang, setSelectedLang] = useState<Record<string, string | undefined>>({
    [DistributionChannel.Native]: undefined,
    [DistributionChannel.Email]: undefined,
  })

  const templatesByChannel = useMemo(
    () => ({
      [DistributionChannel.Native]: row.templates.filter((t) => t.channel === DistributionChannel.Native),
      [DistributionChannel.Email]: row.templates.filter((t) => t.channel === DistributionChannel.Email),
    }),
    [row.templates]
  )

  const missingLangs = useMemo(
    () => ({
      [DistributionChannel.Native]: TEMPLATE_LANGS.filter(
        (lang) => !templatesByChannel[DistributionChannel.Native].some((t) => t.language === lang)
      ),
      [DistributionChannel.Email]: TEMPLATE_LANGS.filter(
        (lang) => !templatesByChannel[DistributionChannel.Email].some((t) => t.language === lang)
      ),
    }),
    [templatesByChannel]
  )

  const selectedTemplate = useCallback(
    (channel: DistributionChannel) =>
      templatesByChannel[channel].find(
        (t) => t.language.toLowerCase().trim() === selectedLang[channel]?.toLowerCase().trim()
      ),
    [templatesByChannel, selectedLang]
  )

  function selectLang(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, lang: string, channel: DistributionChannel) {
    e.stopPropagation()
    setSelectedLang((prev) => {
      const next = prev[channel] === lang ? undefined : lang
      return { ...prev, [channel]: next }
    })
  }

  useLayoutEffect(() => {
    const count = Object.values(selectedLang).filter(Boolean).length
    onEditorToggle(count)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLang])

  function langButtons(missing: string[], channel: DistributionChannel) {
    return (
      <div className="langBtns">
        {TEMPLATE_LANGS.map((lang) => {
          const isDefault = lang === '*'
          const isActive = selectedLang[channel] === lang
          if (missing.includes(lang)) {
            return (
              <button
                key={lang}
                type="button"
                className={`${isActive ? 'isActive' : ''} isMissing`}
                onClick={(e) => selectLang(e, lang, channel)}
              >
                {isDefault ? <i className="icon-website" /> : lang.toUpperCase()} <i className="icon-add" />
              </button>
            )
          }
          return (
            <button
              key={lang}
              type="button"
              className={isActive ? 'isActive' : undefined}
              onClick={(e) => selectLang(e, lang, channel)}
            >
              {isDefault ? <i className="icon-website" /> : lang.toUpperCase()} <i className="icon-edit" />
            </button>
          )
        })}
      </div>
    )
  }

  function channelRow(channel: DistributionChannel) {
    const cfg = CHANNEL_CONFIG[channel]
    const selected = selectedTemplate(channel)
    const missing = missingLangs[channel]
    const currentLang = selectedLang[channel]
    const selectedLangIsMissing = !!currentLang && missing.includes(currentLang)

    return (
      <>
        <div className={cfg.className}>
          <div className="flex">
            <i className={cfg.icon} />
            <div>{row.name}</div>
          </div>
          {langButtons(missing, channel)}
        </div>
        {(!!selected || selectedLangIsMissing) && currentLang && (
          <TemplateEditor template={selected} draftMeta={{ name: row.name, channel, language: currentLang }} />
        )}
      </>
    )
  }

  return (
    <div className="groupDetails">
      {channelRow(DistributionChannel.Native)}
      {channelRow(DistributionChannel.Email)}
    </div>
  )
}
